import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnSizingState,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type TableMeta,
  type Table as TanstackTable,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { Loader2Icon } from 'lucide-react';
import { Fragment } from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import { ScrollArea, ScrollBar } from '~shared/shadcn/ui/scroll-area';
import { MobileTableCardList } from './mobile-table-card-list';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';

type CatchAll = any;

export interface DataTableProps<TData extends Record<string, CatchAll>> {
  columns: Array<ColumnDef<TData, CatchAll>>;
  data: Array<TData>;

  // data: Array<T>;
  // columns: Array<ColumnDef<T, CatchAll>>;

  meta?: TableMeta<TData>;

  // sorting state
  sorting?: SortingState;
  onSortingChange?: React.Dispatch<React.SetStateAction<SortingState>>;

  pagination?: PaginationState;
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>;
  manualPagination?: boolean;
  rowCount?: number;

  // column order state
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: React.Dispatch<React.SetStateAction<ColumnOrderState>>;

  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: React.Dispatch<
    React.SetStateAction<VisibilityState>
  >;

  rowSelection?: RowSelectionState;
  onRowSelectionChange?: React.Dispatch<
    React.SetStateAction<RowSelectionState>
  >;

  // use columnSizing and setColumnSizing to control column sizing, use them together
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: React.Dispatch<
    React.SetStateAction<ColumnSizingState>
  >;

  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: React.Dispatch<
    React.SetStateAction<ColumnFiltersState>
  >;
  manualFiltering?: boolean;

  expanded?: ExpandedState;
  onExpandedChange?: React.Dispatch<React.SetStateAction<ExpandedState>>;
  renderSubComponent?: (row: Row<TData>) => React.ReactNode;

  renderHeader?: (table: TanstackTable<TData>) => React.ReactNode;
  renderMobileList?: (table: TanstackTable<TData>) => React.ReactNode;
  renderFooter?: (table: TanstackTable<TData>) => React.ReactNode;

  autoResetPageIndex?: boolean;
  isUpdating?: boolean;
  className?: string;

  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  manualSorting?: boolean;
  getRowId?: (row: TData, index: number) => string;
}

export function DataTable<TData extends Record<string, CatchAll>>({
  data,
  columns,

  sorting,
  onSortingChange,

  columnFilters,
  onColumnFiltersChange,
  manualFiltering,

  columnVisibility,
  onColumnVisibilityChange,

  rowSelection,
  onRowSelectionChange,

  columnSizing,
  onColumnSizingChange,
  enableRowSelection,

  columnOrder,
  onColumnOrderChange,

  pagination,
  onPaginationChange,
  rowCount,
  manualPagination,
  manualSorting,

  expanded,
  onExpandedChange,
  renderSubComponent,

  meta,
  autoResetPageIndex,
  renderHeader,
  renderMobileList,
  renderFooter,

  isUpdating,
  className,
  getRowId,
}: DataTableProps<TData>) {
  const expandedRowRenderer =
    renderSubComponent != null && onExpandedChange != null
      ? renderSubComponent
      : null;

  const table = useReactTable({
    data,
    columns,
    ...(getRowId ? { getRowId } : {}),

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(pagination && onPaginationChange
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
    ...(expandedRowRenderer != null
      ? { getExpandedRowModel: getExpandedRowModel() }
      : {}),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableRowSelection,

    state: {
      ...(columnFilters ? { columnFilters } : {}),
      ...(columnOrder ? { columnOrder } : {}),
      ...(columnSizing ? { columnSizing } : {}),
      ...(columnVisibility ? { columnVisibility } : {}),
      ...(pagination && onPaginationChange ? { pagination } : {}),
      ...(rowSelection ? { rowSelection } : {}),
      ...(sorting ? { sorting } : {}),
      ...(expandedRowRenderer != null ? { expanded: expanded ?? {} } : {}),
    },

    ...(columnFilters && onColumnFiltersChange
      ? { onColumnFiltersChange }
      : {}),
    ...(columnOrder && onColumnOrderChange ? { onColumnOrderChange } : {}),
    ...(columnSizing && onColumnSizingChange ? { onColumnSizingChange } : {}),
    ...(columnVisibility && onColumnVisibilityChange
      ? { onColumnVisibilityChange }
      : {}),
    ...(pagination && onPaginationChange ? { onPaginationChange } : {}),
    ...(rowSelection && onRowSelectionChange ? { onRowSelectionChange } : {}),
    ...(sorting && onSortingChange ? { onSortingChange } : {}),
    ...(expandedRowRenderer != null ? { onExpandedChange } : {}),

    // columnResizeMode: 'onChange',
    // enableRowSelection: true,
    // enableSortingRemoval: false,

    rowCount,

    manualPagination: manualPagination ?? false,
    manualFiltering: manualFiltering ?? false,
    manualSorting: manualSorting ?? false,

    autoResetPageIndex,

    meta,
  });

  return (
    <div className={cn('flex h-full min-w-0 flex-col', className)}>
      {renderHeader ? renderHeader(table) : null}

      {renderMobileList ? (
        renderMobileList(table)
      ) : (
        <MobileTableCardList table={table} isUpdating={isUpdating} />
      )}

      <ScrollArea
        className={cn('relative hidden min-w-0 flex-1 rounded-md border md:block')}
      >
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className='sticky top-0 z-10 border-b border-border bg-background'
                        colSpan={header.colSpan}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow
                      data-state={
                        onRowSelectionChange && row.getIsSelected() && 'selected'
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {expandedRowRenderer != null && row.getIsExpanded() ? (
                      <TableRow className='hover:bg-transparent'>
                        <TableCell
                          colSpan={row.getVisibleCells().length}
                          className='p-0'
                        >
                          {expandedRowRenderer(row)}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className='h-24 text-center'
                  >
                    {isUpdating ? '' : 'No results.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation='horizontal' />
          {/* <ScrollBar orientation="vertical" /> */}
        </>
        {isUpdating ? (
          <div className='absolute inset-0 flex items-center justify-center bg-black/5 pt-10'>
            <Loader2Icon className='size-8 animate-spin' />
          </div>
        ) : null}
      </ScrollArea>
      {renderFooter ? renderFooter(table) : null}
    </div>
  );
}
